import React, { ReactNode, Fragment } from 'react';
import twttr from 'twitter-text';

export const sanitizeAndParseTextToHtml = (htmlString: string): ReactNode[] => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');
  const elements = doc.body.childNodes;

  const processNode = (node: Node, index: number): JSX.Element | null => {
    if (node.nodeType === Node.TEXT_NODE) {
      return <Fragment key={`text-${index}`}>{node.textContent}</Fragment>;
    } else if (
      node.nodeType === Node.ELEMENT_NODE &&
      node.nodeName.toLowerCase() === 'a' &&
      (node as HTMLElement).hasAttribute('href')
    ) {
      const anchor = node as HTMLAnchorElement;
      return (
        <a href={anchor.getAttribute('href') ?? undefined} key={`link-${index}`}>
          {Array.from(anchor.childNodes).map((child, childIndex) => processNode(child, childIndex))}
        </a>
      );
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      return (
        <Fragment key={`element-${index}`}>
          {Array.from(node.childNodes).map((child, childIndex) => processNode(child, childIndex))}
        </Fragment>
      );
    } else {
      return null;
    }
  };

  return Array.from(elements)
    .map((element, index) => processNode(element, index))
    .filter((item): item is JSX.Element => item !== null);
};

export const convertLinksToAnchorTags = (text: string): JSX.Element => {
  const urlEntities: twttr.UrlEntity[] = twttr.extractUrlsWithIndices(text).map(url => ({
    ...url,
    display_url: url.url,
    expanded_url: url.url
  }));

  let lastIndex = 0;
  const parsedText = [];

  for (const entity of urlEntities) {
    // Add the text before the URL
    if (entity.indices[0] > lastIndex) {
      parsedText.push(text.substring(lastIndex, entity.indices[0]));
    }

    // Check if the URL already has a protocol
    const hasProtocol = /^https?:\/\//.test(entity.url);

    // Add the URL as an anchor
    parsedText.push(
      <a key={entity.url} href={hasProtocol ? entity.url : `//${entity.url}`} target={'_blank'} rel={'noreferrer'}>
        {entity.display_url}
      </a>
    );

    lastIndex = entity.indices[1];
  }

  // Add any remaining text after the last URL
  if (lastIndex < text.length) {
    parsedText.push(text.substring(lastIndex));
  }

  return <>{parsedText}</>;
};

export const toTitleCase = (text: string) => {
  return text.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
};
